import React, { forwardRef, useCallback, useEffect, useRef } from "react";
import {
  FieldTitle,
  InputHelperText,
  ResettableTextField,
  ValidationErrorMessage,
} from "react-admin";
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  UseFormStateReturn,
} from "react-hook-form";
import { HookFormInputProps } from "./HookFormInputProps";
import Inputmask from "inputmask";

interface Props<TRecord> extends HookFormInputProps<TRecord> {
  margin?: "none" | "dense" | "normal";
  variant?: "standard" | "outlined" | "filled";
  mask?: string;
}

interface FormTextInputProps<TRecord extends FieldValues>
  extends HookFormInputProps<TRecord> {
  renderProps: {
    field: ControllerRenderProps<TRecord, Path<TRecord>>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<TRecord>;
  };
  margin?: "none" | "dense" | "normal";
  variant?: "standard" | "outlined" | "filled";
  mask?: string;
}

declare module "react" {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

const FormTextInputComponent = <TRecord extends FieldValues>(
  {
    renderProps,
    options,
    name,
    label,
    rules,
    variant,
    margin,
    mask,
  }: Omit<FormTextInputProps<TRecord>, "control">,
  ref: React.Ref<unknown>
) => {
  const inputRef = useRef<HTMLInputElement>();
  const setRefs = useCallback(
    (element: HTMLInputElement) => {
      inputRef.current = element;
      renderProps.field.ref(element);
    },
    [renderProps.field]
  );

  useEffect(() => {
    if (!mask) {
      return;
    }

    if (!inputRef.current) {
      return;
    }

    var im = new Inputmask(mask);
    im.mask(inputRef.current);
  });

  return (
    <ResettableTextField
      {...options}
      source={name}
      label={
        label !== "" &&
        label !== false && (
          <FieldTitle
            label={label}
            source={name}
            isRequired={!!rules?.required}
          />
        )
      }
      error={
        !!(renderProps.fieldState.isTouched && renderProps.fieldState.error)
      }
      helperText={
        <InputHelperText
          touched={renderProps.fieldState.isTouched}
          error={
            (renderProps.fieldState.error as any)
              ?.message as ValidationErrorMessage
          }
          helperText={(renderProps.fieldState.error as any)?.message as string}
        />
      }
      variant={options?.variant ?? variant}
      margin={options?.margin ?? margin}
      {...renderProps.field}
      ref={ref}
      inputRef={setRefs}
    />
  );
};

const FormTextInput = forwardRef(FormTextInputComponent);

const HookFormTextInputComponent = <TRecord extends FieldValues>(
  {
    control,
    name,
    defaultValue,
    label,
    rules,
    margin,
    variant,
    options,
    mask,
  }: Props<TRecord>,
  ref: React.Ref<unknown>
) => {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue ?? ""}
      rules={rules}
      render={(renderProps) => {
        return (
          <FormTextInput<TRecord>
            renderProps={renderProps}
            label={label}
            options={options}
            name={name}
            rules={rules}
            margin={margin}
            variant={variant}
            mask={mask}
            ref={ref}
          />
        );
      }}
    />
  );
};

export const HookFormTextInput = React.forwardRef(HookFormTextInputComponent);
